/** Data hooks of the Sketch editor: the editor document, the basemap picture, live numbers, save, Design with AI. */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { api, ApiError, errorMessage, rawFetch, unwrap, type S } from "../api/client";
import { editorPanels, type PanelItem } from "../editor/model.ts";
import { toSketchRoof } from "./roofModel.ts";
import { pickMapsKey, shouldFetchMapsKey } from "../lib/mapsKey.ts";
import { drawTiles } from "./tiles.ts";
import type { Sketch } from "./skeleton.ts";
import { sketchProblemText } from "./messages.ts";
import type { Text } from "./text.ts";
import { useSketchText } from "./useText.ts";
import { noPanelNumbers, previewExtras, sketchEditBody, type SketchDraft } from "./state.ts";

const PREVIEW_DEBOUNCE_MS = 600;

export function useEditorDocument(designId: string, version: number | null) {
  return useQuery({
    queryKey: ["editor", designId, version],
    queryFn: () =>
      unwrap(api.GET("/v1/designs/{design_id}/editor", { params: { path: { design_id: designId }, query: version ? { version } : {} } })),
  });
}

/** An authenticated image (the basemap is behind the API's bearer auth) as an object URL. */
export function useAuthedImage(url: string | undefined | null): string | null {
  return useAuthedImageState(url).url;
}

/** The picture at `url` (fetched with the session) and whether it failed (an error answer is never shown as one). */
export function useAuthedImageState(url: string | undefined | null): { url: string | null; failed: boolean } {
  const [state, setState] = useState<{ url: string | null; failed: boolean }>({ url: null, failed: false });
  useEffect(() => {
    if (!url) return;
    let revoked: string | null = null;
    let cancelled = false;
    rawFetch(url)
      .then((r) => (r.ok ? r.blob() : Promise.reject(new Error(String(r.status)))))
      .then((blob) => {
        if (cancelled) return;
        revoked = URL.createObjectURL(blob);
        setState({ url: revoked, failed: false });
      })
      .catch(() => !cancelled && setState({ url: null, failed: true }));
    return () => {
      cancelled = true;
      if (revoked) URL.revokeObjectURL(revoked);
    };
  }, [url]);
  return state;
}

/**
 * The Sketch's basemap picture: drawn live in the browser from a display-only tile provider (Esri, with the
 * browser key of the maps config), else the server's picture (Google). Returns the picture and the attribution.
 */
export function useBasemapPicture(basemap: S["EditorBasemap"] | null | undefined, fallbackUrl: string | undefined): { url: string | null; attribution: string; failed: boolean } {
  const maps = useQuery({ queryKey: ["config", "maps"], queryFn: () => unwrap(api.GET("/v1/config/maps")), enabled: !!basemap?.tile_url, staleTime: Infinity, retry: 1 });
  const key = maps.data?.arcgis_browser_key ?? null;
  const live = !!basemap?.tile_url && !!key && !!basemap.center;
  const [tiles, setTiles] = useState<string | null>(null);
  useEffect(() => {
    if (!live || !basemap?.center) return;
    let url: string | null = null;
    let cancelled = false;
    drawTiles(basemap.tile_url!, key!, basemap.center.lat, basemap.center.lng, basemap.zoom, basemap.tile_max_zoom ?? 19)
      .then((u) => (cancelled ? URL.revokeObjectURL(u) : ((url = u), setTiles(u))))
      .catch(() => setTiles(null));
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [live, basemap?.tile_url, basemap?.center?.lat, basemap?.center?.lng, basemap?.zoom, basemap?.tile_max_zoom, key]); // eslint-disable-line react-hooks/exhaustive-deps
  const server = useAuthedImageState(live || (basemap?.tile_url && maps.isLoading) ? null : fallbackUrl);
  if (live) return { url: tiles, attribution: basemap!.attribution, failed: false };
  return { url: server.url, attribution: basemap && !basemap.tile_url ? basemap.attribution : "Imagery ©Google", failed: server.failed };
}

export interface LiveNumbers {
  numbers: S["EditorNumbers"];
  irradiance: S["IrradianceOutput"] | null;
  /** The drawn roof's setbacks (where modules may go), as the server computes them. */
  setbacks: S["SetbacksOutput"] | null;
  conflicts: S["PanelConflict"][];
  pricing: boolean;
  error: string | null;
  /** The modules' solar access as the server shaded them (by `placeKey`), and the draft they are for. */
  access: Map<string, number> | null;
  for: SketchDraft | null;
}

/** A module's place as the API echoes it (face and centre rounded as `editorPanels` sends it). */
export const placeKey = (faceId: string, center: readonly number[]) => `${faceId}|${Math.round(center[0]! * 10000)}|${Math.round(center[1]! * 10000)}`;

/** The unsaved roof and modules priced by the API (debounced): the quote engine, sunlight per face. */
export function useLiveNumbers(designId: string, doc: S["EditorDocument"], draft: SketchDraft): LiveNumbers {
  const [state, setState] = useState<LiveNumbers>({ numbers: doc.numbers, irradiance: null, setbacks: null, conflicts: [], pricing: false, error: null, access: null, for: null });
  const request = useRef(0);
  const tx = useSketchText();
  useEffect(() => {
    if (!draft.dirty.roof && !draft.dirty.panels && !draft.dirty.obstacles && !draft.dirty.shade) {
      setState((s) => ({ ...s, numbers: doc.numbers, conflicts: [], pricing: false, error: null, access: null, for: null }));
      return;
    }
    if (!draft.sketch.faces.length) {
      // nothing drawn (all of it deleted): zero, not the last preview's numbers
      ++request.current;
      setState((s) => ({ ...s, numbers: noPanelNumbers(s.numbers), irradiance: null, setbacks: null, conflicts: [], pricing: false, error: null, access: null, for: draft }));
      return;
    }
    const id = ++request.current;
    setState((s) => (s.pricing ? s : { ...s, pricing: true })); // no second render on every move of a drag
    const timer = window.setTimeout(() => {
      const faces = new Set(draft.sketch.faces.map((f) => f.id));
      unwrap(
        api.POST("/v1/designs/{design_id}/editor/preview", {
          params: { path: { design_id: designId } },
          body: { version_number: doc.version_number, panels: editorPanels(draft.panels.filter((p) => faces.has(p.face_id))), sketch: toSketchRoof(draft.sketch), ...previewExtras(draft) },
        }),
      )
        .then(
          (r) =>
            id === request.current &&
            setState({ numbers: r.numbers, irradiance: r.irradiance ?? null, setbacks: r.setbacks ?? null, conflicts: r.conflicts, pricing: false, error: null, access: new Map(r.panels.filter((p) => p.solar_access != null).map((p) => [placeKey(p.face_id, p.center_uv), p.solar_access!])), for: draft }),
        )
        .catch((e) => id === request.current && setState((s) => ({ ...s, pricing: false, error: sketchErrorText(tx, e) })));
    }, PREVIEW_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [draft, designId, doc.version_number, doc.numbers]); // eslint-disable-line react-hooks/exhaustive-deps
  return state;
}

/** An API error of the Sketch in plain words (messages.ts): what is wrong on the map, never the request's fields. */
export function sketchErrorText(tx: Text, e: unknown): string {
  return e instanceof ApiError ? sketchProblemText(tx, errorMessage(e), e.problem.errors) : errorMessage(e);
}

/** Saves the Sketch as a new version. A save the server finds changes nothing (`unchanged`) is no error: no new
 * version, `onUnchanged` instead of `onSaved`. */
export function useSaveSketch(designId: string, doc: S["EditorDocument"], onSaved: (n: number) => void, onUnchanged?: () => void) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ draft, note }: { draft: SketchDraft; note: string }) =>
      unwrap(api.POST("/v1/designs/{design_id}/edits", { params: { path: { design_id: designId } }, body: sketchEditBody(doc.version_number, draft, note) })),
    onSuccess: (r) => {
      if (r.unchanged) return onUnchanged?.();
      for (const key of ["editor", "versions", "design", "designer-leads", "bom"]) qc.invalidateQueries({ queryKey: [key] });
      onSaved(r.version.number);
    },
  });
}

/** "Design with AI": a new automatic design of the same project (1.00 Lux). */
export function useDesignWithAi(designId: string) {
  const qc = useQueryClient();
  const [key] = useState(() => (typeof crypto !== "undefined" && "randomUUID" in crypto ? crypto.randomUUID() : `${Date.now()}`));
  return useMutation({
    mutationFn: () => unwrap(api.POST("/v1/designs/{design_id}/ai-design", { params: { path: { design_id: designId }, header: { "Idempotency-Key": key } } })),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["lux"] });
      qc.invalidateQueries({ queryKey: ["designer-leads"] });
    },
  });
}

export type { PanelItem, Sketch };

// --------------------------------------------------------------------------------------- Sketch Assist
const newKey = () => (typeof crypto !== "undefined" && "randomUUID" in crypto ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`);

/** The Assist bar's helpers: included uses left, prices and the balance, all from the server. */
export function useAssist(designId: string, enabled = true) {
  return useQuery({
    queryKey: ["assist", designId],
    queryFn: () => unwrap(api.GET("/v1/designs/{design_id}/assist", { params: { path: { design_id: designId } } })),
    enabled,
    staleTime: 30_000,
  });
}

/** Roof hints for the faces as drawn now; one Idempotency-Key per click (a retried request is one use). Bought once
 * per design: after the first, the server matches from the planes it kept and charges nothing (`use.reused`). */
export function useRoofHints(designId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: S["RoofHintsRequest"]) =>
      unwrap(api.POST("/v1/designs/{design_id}/assist/roof-hints", { params: { path: { design_id: designId }, header: { "Idempotency-Key": newKey() } }, body })),
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ["assist", designId] });
      qc.invalidateQueries({ queryKey: ["lux"] });
    },
  });
}

/** A fresh Idempotency-Key (the page session's 3D surroundings key, assist.ts `AssistPageSession`). */
export const newAssistKey = newKey;

/** A 3D surroundings session: `key` is the page session's (kept by SketchPage across the editor's remounts), so
 * starting again on this page answers the same session and charges nothing. */
export function useSurroundings(designId: string, key: string) {
  const qc = useQueryClient();
  const start = useMutation({
    mutationFn: () => unwrap(api.POST("/v1/designs/{design_id}/assist/surroundings", { params: { path: { design_id: designId }, header: { "Idempotency-Key": key } } })),
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ["assist", designId] });
      qc.invalidateQueries({ queryKey: ["lux"] });
    },
  });
  const failed = useMutation({
    mutationFn: ({ useId, reason }: { useId: string; reason: string }) =>
      unwrap(api.POST("/v1/designs/{design_id}/assist/uses/{use_id}/failed", { params: { path: { design_id: designId, use_id: useId } }, body: { reason: reason.slice(0, 300) } })),
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ["assist", designId] });
      qc.invalidateQueries({ queryKey: ["lux"] });
    },
  });
  return { start, failed };
}

/** The Maps browser key for Google's 3D tiles: config.js's, else the API's (lib/mapsKey.ts). `missing`: asked and
 * there is none, so the 3D surroundings are not offered. */
export function useMapsBrowserKey(configured: string, wanted: boolean): { key: string; missing: boolean } {
  const maps = useQuery({ queryKey: ["config", "maps"], queryFn: () => unwrap(api.GET("/v1/config/maps")), enabled: shouldFetchMapsKey(configured, wanted), staleTime: Infinity, retry: 1 });
  const key = pickMapsKey(configured, maps.data);
  return { key, missing: !key && (maps.isSuccess || maps.isError) };
}

// ------------------------------------------------------------------------- the design's basemap (phase 4)
/** The design's basemap choices (providers at its site, its uploaded pictures) and which one it shows. */
export function useDesignBasemap(designId: string, enabled: boolean) {
  return useQuery({
    queryKey: ["basemap", designId],
    queryFn: () => unwrap(api.GET("/v1/designs/{design_id}/basemap", { params: { path: { design_id: designId } } })),
    enabled,
  });
}

/** Choose, upload, place and remove the design's basemap pictures; each refreshes the choices and the editor's
 * picture (the editor document carries the basemap it shows). */
export function useBasemapActions(designId: string) {
  const qc = useQueryClient();
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ["basemap", designId] });
    void qc.invalidateQueries({ queryKey: ["editor", designId] });
  };
  const choose = useMutation({
    mutationFn: (body: S["DesignBasemapChoice"]) => unwrap(api.PUT("/v1/designs/{design_id}/basemap", { params: { path: { design_id: designId } }, body })),
    onSuccess: refresh,
  });
  const upload = useMutation({
    mutationFn: async (file: File): Promise<S["BasemapUpload"]> => {
      const form = new FormData();
      form.append("file", file);
      const r = await rawFetch(`/api/v1/designs/${designId}/basemap/uploads`, { method: "POST", body: form });
      return (await r.json()) as S["BasemapUpload"];
    },
    onSuccess: refresh,
  });
  const align = useMutation({
    mutationFn: ({ id, body }: { id: string; body: S["BasemapAlignment"] }) =>
      unwrap(api.PUT("/v1/designs/{design_id}/basemap/uploads/{upload_id}", { params: { path: { design_id: designId, upload_id: id } }, body })),
    onSuccess: refresh,
  });
  const remove = useMutation({
    mutationFn: (id: string) => rawFetch(`/api/v1/designs/${designId}/basemap/uploads/${id}`, { method: "DELETE" }),
    onSuccess: refresh,
  });
  return { choose, upload, align, remove };
}

/** The design's notes (comment threads of kind `note`; the Designer's comments API): read, pin, change, close. */
export function useSketchNotes(designId: string) {
  const qc = useQueryClient();
  const key = ["comments", designId];
  const threads = useQuery({
    queryKey: key,
    queryFn: () => unwrap(api.GET("/v1/designs/{design_id}/comments", { params: { path: { design_id: designId } } })),
    refetchInterval: 30_000,
  });
  const put = (t: S["DesignCommentThread"]) =>
    qc.setQueryData(key, (old: S["DesignCommentThreadList"] | undefined) => ({
      items: old ? (old.items.some((x) => x.id === t.id) ? old.items.map((x) => (x.id === t.id ? t : x)) : [...old.items, t]) : [t],
    }));
  const create = useMutation({
    mutationFn: (body: S["DesignCommentThreadCreate"]) => unwrap(api.POST("/v1/designs/{design_id}/comments", { params: { path: { design_id: designId } }, body })),
    onSuccess: put,
  });
  const update = useMutation({
    mutationFn: ({ id, body }: { id: string; body: S["DesignCommentThreadUpdate"] }) =>
      unwrap(api.PATCH("/v1/designs/{design_id}/comments/{thread_id}", { params: { path: { design_id: designId, thread_id: id } }, body })),
    onSuccess: put,
  });
  return { threads, create, update };
}
